/**
 * SupplierCompliancePage — /dashboard/supplier/compliance
 *
 * Compliance Hub: store PAT + PLI, share them to venues/customers, track
 * sends. Free tier uploads. Studio Pass sends. Fully Verified badge lights
 * up when both certs are present and not expired — that badge then
 * propagates to supplier cards and the public profile.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, PaperPlaneTilt, FileArrowUp, Trash, CheckCircle, Warning, XCircle, Lock, CaretDown, CaretUp, Download, Eye } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CERT_META = {
  pat: { label: 'PAT Certificate', blurb: 'Portable Appliance Testing', numberLabel: 'Certificate No.', issuerLabel: 'Issued By' },
  pli: { label: 'PLI Certificate', blurb: 'Public Liability Insurance', numberLabel: 'Policy No.', issuerLabel: 'Provider' },
};

const STATUS_META = {
  active:   { label: 'Active',   Icon: CheckCircle, cls: 'text-emerald-300 bg-emerald-400/15 border-emerald-400/30' },
  expiring: { label: 'Expiring', Icon: Warning,     cls: 'text-amber-300 bg-amber-400/15 border-amber-400/30' },
  expired:  { label: 'Expired',  Icon: XCircle,     cls: 'text-red-300 bg-red-400/15 border-red-400/30' },
};

const SupplierCompliancePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState([]);
  const [isVerified, setIsVerified] = useState(false);
  const [sends, setSends] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [uploadTarget, setUploadTarget] = useState(null); // 'pat' | 'pli' | null
  const [sendModal, setSendModal] = useState(null); // { certIds: [...] }
  const [isPro, setIsPro] = useState(false);

  const refresh = async () => {
    try {
      const [a, b, q] = await Promise.all([
        axios.get(`${API}/suppliers/me/certificates`),
        axios.get(`${API}/suppliers/me/certificate-sends`).catch(() => ({ data: { sends: [] } })),
        axios.get(`${API}/suppliers/me/chat-quota`).catch(() => ({ data: { is_pro: false } })),
      ]);
      setCerts(a.data.certificates || []);
      setIsVerified(!!a.data.is_verified);
      setSends(b.data.sends || []);
      setIsPro(!!q.data.is_pro);
    } catch (e) {
      toast.error('Could not load compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const patCert = certs.find((c) => c.type === 'pat');
  const pliCert = certs.find((c) => c.type === 'pli');

  const onDelete = async (cert) => {
    if (!window.confirm(`Remove your ${CERT_META[cert.type].label}?`)) return;
    try {
      await axios.delete(`${API}/suppliers/me/certificates/${cert.id}`);
      toast.success('Certificate removed');
      refresh();
    } catch { toast.error('Could not remove'); }
  };

  const openSend = (cert) => {
    if (!isPro) {
      toast.error('Upgrade to Studio Pass to send certificates');
      navigate('/studio-pass');
      return;
    }
    setSendModal({ certIds: [cert.id] });
  };

  const openSendAll = () => {
    if (!isPro) {
      toast.error('Upgrade to Studio Pass to send certificates');
      navigate('/studio-pass');
      return;
    }
    const ids = certs.filter((c) => c.status !== 'expired').map((c) => c.id);
    if (!ids.length) {
      toast.error('Add a certificate first');
      return;
    }
    setSendModal({ certIds: ids });
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="supplier-compliance-page">
      <div className="max-w-4xl mx-auto px-5 py-6">
        <button
          onClick={() => navigate('/dashboard/supplier')}
          className="text-white/50 hover:text-white text-sm flex items-center gap-1.5 mb-4"
          data-testid="compliance-back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Dashboard
        </button>

        {/* Hero */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={22} weight="fill" className="text-[#C29D52]" />
            <h1 className="text-3xl font-black tracking-tight">Supplier Compliance Hub</h1>
          </div>
          <p className="text-white/55 text-sm">
            Store and share your PAT testing &amp; Public Liability Insurance — win more bookings by being ready.
          </p>
          {isVerified && (
            <div
              className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full text-xs font-black bg-emerald-400/15 border border-emerald-400/40 text-emerald-200"
              data-testid="verified-badge-pill"
            >
              <CheckCircle size={12} weight="fill" />
              Fully Verified Supplier
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-white/40 text-sm text-center py-10">Loading…</p>
        ) : (
          <>
            {/* Certificates grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="certificates-grid">
              <CertificateCard
                kind="pat"
                cert={patCert}
                onUpload={() => setUploadTarget('pat')}
                onDelete={() => onDelete(patCert)}
                onSend={() => openSend(patCert)}
                isPro={isPro}
              />
              <CertificateCard
                kind="pli"
                cert={pliCert}
                onUpload={() => setUploadTarget('pli')}
                onDelete={() => onDelete(pliCert)}
                onSend={() => openSend(pliCert)}
                isPro={isPro}
              />
            </div>

            {/* Send bundle panel */}
            <div
              className="rounded-2xl border border-[#B89762]/40/20 bg-[#B89762]/30/5 p-5 mb-6"
              data-testid="send-bundle-panel"
            >
              <div className="flex items-start gap-3 mb-2">
                <PaperPlaneTilt size={18} weight="fill" className="text-[#C29D52] mt-0.5" />
                <div>
                  <p className="font-black">Send certificates to a venue or customer</p>
                  <p className="text-white/60 text-xs mt-0.5">
                    Quickly share your certificates — they land as a branded email with PDF attachments.
                  </p>
                </div>
              </div>
              <button
                onClick={openSendAll}
                disabled={!certs.length}
                className="w-full sm:w-auto mt-2 py-2.5 px-4 rounded-xl text-sm font-bold text-white disabled:opacity-50 flex items-center gap-2"
                style={{ background: isPro ? 'linear-gradient(135deg, #B89762, #C29D52)' : 'rgba(255,255,255,0.08)' }}
                data-testid="send-all-btn"
              >
                {isPro ? <PaperPlaneTilt size={14} weight="fill" /> : <Lock size={14} weight="fill" />}
                {isPro ? 'Send certificates' : 'Send (Studio Pass)'}
              </button>
            </div>

            {/* Send history */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="send-history-panel">
              <button
                onClick={() => setHistoryOpen(!historyOpen)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/[0.03]"
              >
                <div>
                  <p className="font-black">Certificate send history</p>
                  <p className="text-white/40 text-xs">Track all certificates you've sent to venues and customers.</p>
                </div>
                {historyOpen ? <CaretUp size={14} className="text-white/40" /> : <CaretDown size={14} className="text-white/40" />}
              </button>
              {historyOpen && (
                <div className="px-5 pb-4" data-testid="send-history-list">
                  {!isPro ? (
                    <div className="text-center py-6">
                      <Lock size={24} weight="fill" className="mx-auto mb-2 text-[#C29D52]" />
                      <p className="text-white/70 text-sm font-bold">Send history is a Studio Pass feature</p>
                      <p className="text-white/40 text-xs mt-1">Upgrade to send certificates and see delivery history.</p>
                    </div>
                  ) : !sends.length ? (
                    <p className="text-white/40 text-xs text-center py-5">No sends yet.</p>
                  ) : (
                    <ul className="divide-y divide-white/5">
                      {sends.map((s) => (
                        <li
                          key={s.id}
                          className="py-3 flex items-start gap-3 text-xs"
                          data-testid={`send-row-${s.id}`}
                        >
                          <PaperPlaneTilt size={14} weight="fill" className="text-[#C29D52] mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-white">{s.recipient_name || s.recipient_email}</p>
                            <p className="text-white/50 truncate">{s.recipient_email}</p>
                            <div className="flex gap-1 mt-1">
                              {(s.certificate_types || []).map((t) => (
                                <span
                                  key={t}
                                  className="px-1.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-[#B89762]/30/10 text-[#C29D52]/85 border border-[#B89762]/40/20"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-white/60">{formatDateShort(s.sent_at)}</p>
                            <p className="text-emerald-300 font-bold">Sent</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {uploadTarget && (
        <UploadModal
          kind={uploadTarget}
          onClose={() => setUploadTarget(null)}
          onUploaded={() => { setUploadTarget(null); refresh(); }}
        />
      )}

      {sendModal && (
        <SendModal
          allCerts={certs}
          selectedIds={sendModal.certIds}
          onClose={() => setSendModal(null)}
          onSent={() => { setSendModal(null); refresh(); }}
        />
      )}
    </div>
  );
};

const CertificateCard = ({ kind, cert, onUpload, onDelete, onSend, isPro }) => {
  const meta = CERT_META[kind];
  if (!cert) {
    return (
      <div
        className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex flex-col"
        data-testid={`cert-card-empty-${kind}`}
      >
        <p className="text-[11px] font-black uppercase tracking-wider text-[#C29D52]/85/70">{meta.blurb}</p>
        <p className="font-black text-lg mt-0.5">{meta.label}</p>
        <p className="text-white/50 text-xs mt-1 mb-5">Not uploaded yet.</p>
        <button
          onClick={onUpload}
          className="mt-auto w-full py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid={`upload-${kind}-btn`}
        >
          <FileArrowUp size={14} weight="fill" /> Add {meta.label}
        </button>
      </div>
    );
  }
  const status = STATUS_META[cert.status] || STATUS_META.active;
  const StatusIcon = status.Icon;
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex flex-col"
      data-testid={`cert-card-${kind}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-wider text-[#C29D52]/85/70">{meta.blurb}</p>
          <p className="font-black text-lg mt-0.5">{meta.label}</p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${status.cls}`}>
          <StatusIcon size={11} weight="fill" /> {status.label}
        </span>
      </div>

      <div className="space-y-2 text-xs mb-4">
        {cert.certificate_number && (
          <div>
            <p className="text-white/40 uppercase tracking-wider text-[10px] font-bold">{meta.numberLabel}</p>
            <p className="text-white/90 font-bold">{cert.certificate_number}</p>
          </div>
        )}
        {cert.expiry_date && (
          <div>
            <p className="text-white/40 uppercase tracking-wider text-[10px] font-bold">Expiry Date</p>
            <p className="text-white/90 font-bold">
              {formatDateLong(cert.expiry_date)}{' '}
              <span className={cert.status === 'expired' ? 'text-red-300' : cert.status === 'expiring' ? 'text-amber-300' : 'text-white/50'}>
                ({relativeDays(cert.expiry_date)})
              </span>
            </p>
          </div>
        )}
        {cert.issuer && (
          <div>
            <p className="text-white/40 uppercase tracking-wider text-[10px] font-bold">{meta.issuerLabel}</p>
            <p className="text-white/90 font-bold">{cert.issuer}</p>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-auto">
        <a
          href={`${API}/suppliers/me/certificates/${cert.id}/file`}
          target="_blank"
          rel="noreferrer"
          className="flex-1 py-2 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center gap-1.5"
          data-testid={`view-${kind}-btn`}
        >
          <Eye size={12} weight="fill" /> View
        </a>
        <button
          onClick={onSend}
          className={`flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 text-white ${!isPro ? 'opacity-70' : ''}`}
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid={`send-${kind}-btn`}
        >
          {isPro ? <PaperPlaneTilt size={12} weight="fill" /> : <Lock size={12} weight="fill" />}
          Send
        </button>
        <button
          onClick={onUpload}
          className="py-2 px-3 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10"
          data-testid={`replace-${kind}-btn`}
          title="Replace"
        >
          <Download size={12} weight="bold" style={{ transform: 'rotate(180deg)' }} />
        </button>
        <button
          onClick={onDelete}
          className="py-2 px-3 rounded-lg text-xs font-bold text-white/40 hover:text-red-400 bg-white/5 hover:bg-white/10 border border-white/10"
          data-testid={`delete-${kind}-btn`}
          title="Remove"
        >
          <Trash size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
};

const UploadModal = ({ kind, onClose, onUploaded }) => {
  const meta = CERT_META[kind];
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    file: null,
    certificate_number: '',
    issuer: '',
    expiry_date: '',
  });
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    if (!form.file) { toast.error('Pick a file first'); return; }
    if (busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('type', kind);
      if (form.certificate_number) fd.append('certificate_number', form.certificate_number);
      if (form.issuer) fd.append('issuer', form.issuer);
      if (form.expiry_date) fd.append('expiry_date', form.expiry_date);
      await axios.post(`${API}/suppliers/me/certificates`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${meta.label} uploaded`);
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="upload-cert-modal"
    >
      <div className="w-full sm:max-w-md bg-[#15161B] border border-white/10 sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 pt-5 pb-3 border-b border-white/5 flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-black text-[#C29D52]/85/80">Upload</p>
            <h3 className="font-black text-lg mt-0.5">{meta.label}</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white -mr-2 p-2">×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1.5">File (PDF, PNG or JPG — max 10 MB)</label>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full py-3 rounded-lg border border-dashed border-white/15 bg-white/[0.04] hover:bg-white/[0.07] text-sm text-white/70"
              data-testid="cert-file-picker"
            >
              {form.file ? form.file.name : 'Choose file'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
              className="hidden"
            />
          </div>
          <div>
            <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1.5">{meta.numberLabel}</label>
            <input
              type="text"
              value={form.certificate_number}
              onChange={(e) => setForm({ ...form, certificate_number: e.target.value })}
              placeholder={kind === 'pat' ? 'PAT-2026-0457' : 'PLI-5508897'}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B89762]/40/40"
              data-testid="cert-number-input"
            />
          </div>
          <div>
            <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1.5">{meta.issuerLabel}</label>
            <input
              type="text"
              value={form.issuer}
              onChange={(e) => setForm({ ...form, issuer: e.target.value })}
              placeholder={kind === 'pat' ? 'Test & Tag Services Ltd' : 'Hiscox Insurance Company'}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B89762]/40/40"
              data-testid="cert-issuer-input"
            />
          </div>
          <div>
            <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1.5">Expiry Date</label>
            <input
              type="date"
              value={form.expiry_date}
              onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B89762]/40/40"
              data-testid="cert-expiry-input"
            />
          </div>
        </div>
        <div className="border-t border-white/5 px-5 py-3 flex gap-2">
          <button
            onClick={onSubmit}
            disabled={busy || !form.file}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="cert-upload-submit"
          >
            {busy ? 'Uploading…' : 'Save certificate'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-bold bg-white/5 border border-white/10 hover:bg-white/10"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const SendModal = ({ allCerts, selectedIds: initialIds, onClose, onSent }) => {
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('Hi, please find my certificates attached for your records.');
  const [ids, setIds] = useState(initialIds);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    if (!ids.length) { toast.error('Pick at least one certificate'); return; }
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/suppliers/me/certificates/send`, {
        recipient_name: recipientName || null,
        recipient_email: recipientEmail,
        message: message || null,
        certificate_ids: ids,
      });
      toast.success('Certificates are on their way');
      onSent();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'upgrade_required') {
        toast.error(detail.message || 'Upgrade to Studio Pass to send certificates');
      } else {
        toast.error(
          detail?.message
            || (typeof detail === 'string' ? detail : null)
            || 'Could not send — please try again'
        );
      }
    } finally {
      setBusy(false);
    }
  };
  const toggle = (id) => setIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="send-cert-modal"
    >
      <div className="w-full sm:max-w-md bg-[#15161B] border border-white/10 sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 pt-5 pb-3 border-b border-white/5 flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-black text-[#C29D52]/85/80">Send certificates</p>
            <h3 className="font-black text-lg mt-0.5">To venue or customer</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white -mr-2 p-2">×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Recipient name"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B89762]/40/40"
              data-testid="send-recipient-name"
            />
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="Recipient email"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B89762]/40/40"
              data-testid="send-recipient-email"
            />
          </div>
          <div>
            <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1.5">Certificates</label>
            <div className="flex flex-col gap-1.5">
              {allCerts.filter((c) => c.status !== 'expired').map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm cursor-pointer hover:bg-white/[0.07]"
                >
                  <input
                    type="checkbox"
                    checked={ids.includes(c.id)}
                    onChange={() => toggle(c.id)}
                    data-testid={`send-toggle-${c.type}`}
                  />
                  <span className="font-bold">{CERT_META[c.type].label}</span>
                  {c.certificate_number && <span className="text-white/40 text-xs">· {c.certificate_number}</span>}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1.5">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B89762]/40/40 resize-none"
              data-testid="send-message-input"
            />
          </div>
        </div>
        <div className="border-t border-white/5 px-5 py-3 flex gap-2">
          <button
            onClick={send}
            disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="send-submit-btn"
          >
            <PaperPlaneTilt size={14} weight="fill" /> {busy ? 'Sending…' : 'Send certificates'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-bold bg-white/5 border border-white/10 hover:bg-white/10"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

function formatDateLong(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}
function formatDateShort(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  } catch { return iso; }
}
function relativeDays(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso);
    const diff = Math.floor((then - new Date()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return `${Math.abs(diff)} days ago`;
    if (diff === 0) return 'today';
    return `in ${diff} days`;
  } catch { return ''; }
}

export default SupplierCompliancePage;
